import { calculateBoundingBoxCornerPoints } from "@nova/calculate-geospatial"
import { AreaUnit, DistanceUnit } from "@nova/constants"
import { Coordinate, Ruler } from "@nova/protobuf"
import { createSelector } from "@reduxjs/toolkit"
import { BoundingBoxLayerData } from "plugins/shared/layers/BoundingBoxLayer.class"
import { RootState } from "integrations/redux/rootReducer"
import { memoize } from "nodash"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"
import { getAncestors } from "panels/content/side-panel/utils"
import { PolygonGeometry } from "plugins/shared/layers/GeometryLinesLayer.class"
import { selectRulerEditingData } from "plugins/Toolbox/Ruler/slice"
import { selectRulerTemplate } from "plugins/Toolbox/tool-registry"
import { selectHoveredBoundingBoxId } from "../flights/selectors"

const root = (state: RootState) => state.project.data.rulers

export const selectAllRulers = (state: RootState) => root(state) as Ruler[]
export const selectNonArchivedRulers = createSelector([selectAllRulers], (rulers) =>
    rulers.filter((ruler) => !ruler.archivedTimestamp)
)
export const selectRulersMap = createSelector([selectAllRulers], (rulers) => {
    return rulers.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, Ruler>
    )
})

export const selectRulerById = memoize((id: string) =>
    createSelector([selectRulersMap], (rulers) => {
        return rulers[id]
    })
)

export const selectVisibleRulers = createSelector(
    [selectNonArchivedRulers, selectAllEntitiesMap],
    (rulers, entityMap) =>
        rulers.filter(
            (ruler) =>
                ruler.visible &&
                !getAncestors(ruler.id, entityMap).find((ancestor) => !ancestor.visible)
        )
)

export const selectAllRulerBoundingBoxes = createSelector([selectVisibleRulers], (rulers) =>
    rulers.reduce((boundingBoxes, ruler) => {
        const allPoints: Coordinate[] = ruler.coordinates

        if (allPoints.length > 0) {
            const box = calculateBoundingBoxCornerPoints(allPoints)
            const polygon = box.polygon

            boundingBoxes.push({
                id: ruler.id,
                polygon: box.polygon,
                path: [...polygon, polygon[0]],
                area: box.area,
            })
        }
        return boundingBoxes
    }, [] as BoundingBoxLayerData[])
)

export const selectRulersToDraw = createSelector([selectVisibleRulers], (rulers) =>
    rulers.map((ruler, i) => ({
        ...ruler,
        distanceUnit: ruler.distanceUnit as DistanceUnit,
        areaUnit: ruler.areaUnit as AreaUnit,
        idx: i,
    }))
)

export const selectRulersWithMockToDraw = createSelector(
    [selectVisibleRulers, selectRulerEditingData, selectRulerTemplate],
    (rulers, editingData, template) => {
        const mapped = rulers.map((ruler, i) => ({
            ...ruler,
            distanceUnit: ruler.distanceUnit as DistanceUnit,
            areaUnit: ruler.areaUnit as AreaUnit,
            idx: i,
            deckGlIdx: i,
        })) as PolygonGeometry[]
        if (editingData) {
            mapped.push({
                ...editingData.mockedRuler,
                idx: mapped.length,
                deckGlIdx: mapped.length,
                ...template,
            })
        }
        return mapped
    }
)

export const selectHoveredRulerId = createSelector(
    [selectNonArchivedRulers, selectHoveredBoundingBoxId],
    (rulers, id) => (rulers.some((ruler) => ruler.id === id) ? id : undefined)
)

export const selectRulerDistanceUnitById = memoize((id: string) =>
    createSelector(
        [selectRulerById(id)],
        (ruler) => ruler?.distanceUnit as DistanceUnit | undefined
    )
)

export const selectRulerAreaUnitById = memoize((id: string) =>
    createSelector([selectRulerById(id)], (ruler) => ruler?.areaUnit as AreaUnit | undefined)
)
